import { useEffect, useState } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { Button } from 'src/components/ui/button';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'src/store';
import {
  useGetConfigurationSettingsPromptQuery,
  useUpdateConfigurationSettingsPromptMutation,
} from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { FormProvider, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import {
  escalationWorkflowSchema,
  EscalationFormValues,
  getEscalationFormValues,
} from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep6/escalationWorkflowForm';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';
import { Skeleton } from 'src/components/ui/skeleton';
import { validateWorkflowActions } from 'src/helpers/validateWorkflowActions';
import { getWorkflowRequestAction } from 'src/pages/ConfigurationPage/WorkflowsTab/getWorkflowRequestAction';
import { EscalatedList } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep6/EscalatedList/EscalatedList';
import { API_VERSION } from 'src/constants/api-routes';
import { useCreationFlowSettings } from 'src/pages/ProjectPage/CreateProjectSteps/useCreationFlowSettings';
import { getCreateProjectStepPath } from 'src/pages/ProjectPage/CreateProjectSteps/createProjectStepPath';
import { getApiVersion } from 'src/utils/apiVersion';

const LoadingSkeleton = () => {
  return (
    <div className={'w-[455px] flex flex-col gap-5'}>
      <Skeleton className={'w-full h-4 mb-10'} />
      <Skeleton className={'w-full h-20'} />
      <Skeleton className={'w-full h-96'} />
    </div>
  );
};

const CreateProjectStep6Page = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [inputData, setInputData] = useState(null);
  const [workflow, setWorkflow] = useState(inputData?.pf_default_workflow || null);
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const { id, step } = useParams();
  const loader = useLoader();
  const navigate = useNavigate();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || type;
  const creationFlowState = useAppSelector((s) => s.creationFlowSlices);
  const dispatch = useAppDispatch();
  const version = getApiVersion();
  const isV2 = version === API_VERSION.V2;
  const { settings, activeSettings, isLoadingAllConfigurationSettings } = useCreationFlowSettings(
    id,
    { skip: !id, refetchOnMountOrArgChange: true },
  );
  const [
    updateConfigurationSettings,
    { data: updateConfigurationSettingsData, isLoading: isLoadingUpdateConfigurationSettings },
  ] = useUpdateConfigurationSettingsPromptMutation();
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: id, settings_id: activeSettings?.id },
      {
        skip: !(id && activeSettings),
        refetchOnMountOrArgChange: true,
      },
    );

  useEffect(() => {
    if (
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingUpdateConfigurationSettings ||
      settings === null
    ) {
      setIsLoading(true);
      loader.show();
    } else {
      setIsLoading(false);
      loader.hide();
    }
  }, [
    isLoadingAllConfigurationSettings,
    isLoadingConfigurationSettingsPrompt,
    isLoadingUpdateConfigurationSettings,
    settings,
  ]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      setInputData(configurationSettingsPrompt.input_data);
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    if (inputData) {
      const workflow = inputData?.pf_default_workflow;

      methods.reset(getEscalationFormValues(workflow));

      setWorkflow(workflow);
    }
  }, [inputData]);

  useEffect(() => {
    const config = creationFlowState[id];

    if (config && +config.step < +step) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (updateConfigurationSettingsData) {
      next();
    }
  }, [updateConfigurationSettingsData]);

  const methods = useForm<EscalationFormValues>({
    defaultValues: async () => getEscalationFormValues(workflow),
    resolver: formResolver(escalationWorkflowSchema),
  });

  const submit = (data: EscalationFormValues) => {
    const is = {
      valid: validateWorkflowActions(data, methods.setError),
      dirty: Boolean(Object.keys(methods.formState.dirtyFields).length),
      deployed: activeSettings?.is_deployed,
    };

    if (!is.valid) {
      return;
    }

    if (activeSettings) {
      const action = data.action.map((action) => getWorkflowRequestAction(action));
      const newWorkflow = {
        enabled: workflow.enabled,
        type: workflow.type,
        action: action[0],
      };

      const body = {
        input_data: {
          ...inputData,
          pf_default_workflow: newWorkflow,
        },
      };

      updateConfigurationSettings({ project_id: id, settings_id: activeSettings.id, body });
    }
  };

  const next = () => {
    navigate(getCreateProjectStepPath(id, +step + 1, projectTypeVar), { replace: true });
  };

  const back = () => {
    navigate(getCreateProjectStepPath(id, +step - 1, projectTypeVar), { replace: true });
  };

  return (
    <PageContentWrapper
      menuBarVisible={false}
      className={'create-project-step items-center !gap-5'}
    >
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <>
          <CreateProjectStepsTitle step={isV2 ? +step - 1 : +step} title={'Escalation Workflow'} />
          <CreateProjectHead step={+step} titleIndex={6} />
          <div className={'flex flex-col items-center w-[455px] gap-5'}>
            <FormProvider {...methods}>
              <form
                id={'create-project-step-6'}
                className={'flex flex-col gap-5 w-full min-w-[455px] max-w-[455px]'}
                onSubmit={methods.handleSubmit(submit)}
              >
                <EscalatedList
                  control={methods.control}
                  watch={methods.watch}
                  resetField={methods.resetField}
                  listItemContentClassName={'!bg-white'}
                />
              </form>
            </FormProvider>
            <Button
              form="create-project-step-6"
              type="submit"
              className="min-w-24 w-full flex flex-row h-12 mt-5"
            >
              Continue
            </Button>
            <button
              type="button"
              onClick={back}
              className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
            >
              ← Back
            </button>
          </div>
        </>
      )}
    </PageContentWrapper>
  );
};

export default CreateProjectStep6Page;
